import { LinkContainer } from 'react-router-bootstrap';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { useMsal } from "@azure/msal-react";
import {
    faSearch,
    faVideo,
    faSun,
    faMoon,
    faDesktop,
    faCheck,
    faHouse,
    faBolt,
    faFilm,
    faBookmark,
    faFolder,
    faTags,
    faDatabase,
    faBroom,
    faEllipsis,
    faPlus,
    faLayerGroup,
    faList,
    faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

export const AppNavbar = () => {
    const msal = useMsal();
    const { themeMode, effectiveTheme, setThemeMode } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const logout = async () => {
        await msal.instance.logoutRedirect();
    };

    const openSearch = useCallback(() => {
        if (location.pathname !== '/search') {
            navigate('/search');
        }
        // Ask the search page to focus its input. If it is still mounting,
        // its autofocus handles this instead.
        window.dispatchEvent(new Event('vl:focus-search'));
    }, [location.pathname, navigate]);

    useEffect(() => {
        const handleShortcut = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                openSearch();
            }
        };

        window.addEventListener('keydown', handleShortcut);
        return () => window.removeEventListener('keydown', handleShortcut);
    }, [openSearch]);

    const currentThemeIcon = effectiveTheme === 'dark' ? faMoon : faSun;

    return (
        <Navbar expand="xl" className="app-navbar sticky-top mb-3">
            <Container fluid>
                <LinkContainer to={`/`}>
                    <Navbar.Brand className="d-flex align-items-center gap-2">
                        <span className="app-navbar__brand-icon">
                            <FontAwesomeIcon icon={faVideo} title="Video Library" />
                        </span>
                        <span>Video Library</span>
                    </Navbar.Brand>
                </LinkContainer>
                <Navbar.Toggle aria-controls="navbar-nav" />
                <Navbar.Collapse id="navbar-nav">
                    <Nav className="me-auto">
                        <LinkContainer to={'/'}>
                            <Nav.Link>
                                <FontAwesomeIcon icon={faHouse} className="app-navbar__nav-icon" />
                                <span>Home</span>
                            </Nav.Link>
                        </LinkContainer>
                        <LinkContainer to={'/quick-add'}>
                            <Nav.Link>
                                <FontAwesomeIcon icon={faBolt} className="app-navbar__nav-icon" />
                                <span>Quick Add</span>
                            </Nav.Link>
                        </LinkContainer>
                        <NavDropdown
                            title={
                                <span className="d-inline-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faFilm} className="app-navbar__nav-icon" />
                                    <span>Videos</span>
                                </span>
                            }
                            id="videos-dropdown"
                        >
                            <LinkContainer to={'/videos/add'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faPlus} className="text-secondary" fixedWidth />
                                    <span>Add video</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                            <LinkContainer to={'/videos/add/bulk'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faLayerGroup} className="text-secondary" fixedWidth />
                                    <span>Bulk add videos</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                        </NavDropdown>
                        <NavDropdown
                            title={
                                <span className="d-inline-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faBookmark} className="app-navbar__nav-icon" />
                                    <span>Bookmarks</span>
                                </span>
                            }
                            id="bookmarks-dropdown"
                        >
                            <LinkContainer to={'/bookmarks'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faList} className="text-secondary" fixedWidth />
                                    <span>List bookmarks</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                            <NavDropdown.Divider />
                            <LinkContainer to={'/bookmarks/add'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faPlus} className="text-secondary" fixedWidth />
                                    <span>Add bookmark</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                            <LinkContainer to={'/bookmarks/add/bulk'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faLayerGroup} className="text-secondary" fixedWidth />
                                    <span>Bulk add bookmarks</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                        </NavDropdown>
                        <LinkContainer to={"/cleanup"}>
                            <Nav.Link>
                                <FontAwesomeIcon icon={faBroom} className="app-navbar__nav-icon" />
                                <span>Cleanup</span>
                            </Nav.Link>
                        </LinkContainer>
                        <NavDropdown
                            title={
                                <span className="d-inline-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faEllipsis} className="app-navbar__nav-icon" />
                                    <span>More</span>
                                </span>
                            }
                            id="more-dropdown"
                        >
                            <LinkContainer to={'/categories'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faFolder} className="text-secondary" fixedWidth />
                                    <span>Categories</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                            <LinkContainer to={'/tags'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faTags} className="text-secondary" fixedWidth />
                                    <span>Tags</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                            <NavDropdown.Divider />
                            <LinkContainer to={'/backup'}>
                                <NavDropdown.Item className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faDatabase} className="text-secondary" fixedWidth />
                                    <span>Backup</span>
                                </NavDropdown.Item>
                            </LinkContainer>
                        </NavDropdown>
                    </Nav>
                    <Nav className="align-items-xl-center gap-1 mt-2 mt-xl-0 ms-auto">
                        <Nav.Link
                            as="button"
                            type="button"
                            onClick={openSearch}
                            className="app-navbar__search-btn me-lg-1"
                            aria-label="Search"
                            title="Search (Ctrl/⌘ K)"
                        >
                            <FontAwesomeIcon icon={faSearch} title="Search" />
                            <span className="pe-1">Search...</span>
                        </Nav.Link>
                        <NavDropdown
                            title={<FontAwesomeIcon icon={currentThemeIcon} title={`Theme mode (${themeMode})`} />}
                            id="theme-dropdown"
                            align="end"
                            className="app-navbar__icon-btn"
                        >
                            <NavDropdown.Item
                                onClick={() => setThemeMode('light')}
                                active={themeMode === 'light'}
                                className="d-flex align-items-center justify-content-between gap-3"
                            >
                                <span>
                                    <FontAwesomeIcon icon={faSun} className="me-2" fixedWidth />
                                    Light
                                </span>
                                {themeMode === 'light' && <FontAwesomeIcon icon={faCheck} size="sm" />}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                onClick={() => setThemeMode('dark')}
                                active={themeMode === 'dark'}
                                className="d-flex align-items-center justify-content-between gap-3"
                            >
                                <span>
                                    <FontAwesomeIcon icon={faMoon} className="me-2" fixedWidth />
                                    Dark
                                </span>
                                {themeMode === 'dark' && <FontAwesomeIcon icon={faCheck} size="sm" />}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                onClick={() => setThemeMode('system')}
                                active={themeMode === 'system'}
                                className="d-flex align-items-center justify-content-between gap-3"
                            >
                                <span>
                                    <FontAwesomeIcon icon={faDesktop} className="me-2" fixedWidth />
                                    System
                                </span>
                                {themeMode === 'system' && <FontAwesomeIcon icon={faCheck} size="sm" />}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <Nav.Link onClick={logout} className="text-secondary">
                            <FontAwesomeIcon icon={faRightFromBracket} className="app-navbar__nav-icon" />
                            <span>Logout</span>
                        </Nav.Link>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};